<template>
  <div class="feed-config-page container py-4">
    <!-- Global Loader Overlay -->
    <div
      v-if="loading.global"
      class="loading-overlay d-flex justify-content-center align-items-center"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Site Selector -->
    <div class="mb-4 d-flex align-items-end">
      <label for="siteSelect" class="h6 font-weight-bold mr-2"
        >Selecione o Site:</label
      >
      <select
        id="siteSelect"
        v-model="site"
        @change="onSiteChange"
        class="form-control form-control-sm w-auto mr-2"
        :disabled="loading.sites"
      >
        <option v-for="s in sites" :key="s" :value="s">
          {{ siteLabels[s] || s }}
        </option>
      </select>
      <button
        class="btn btn-outline-primary btn-sm"
        @click="loadAll"
        :disabled="loading.global"
      >
        <span
          v-if="loading.global"
          class="spinner-border spinner-border-sm mr-1"
        ></span>
        Atualizar Dados
      </button>
    </div>

    <h2 class="mb-3 text-primary">
      Configuração: {{ siteLabels[site] || site }}
    </h2>

    <!-- Settings (Stock) -->
    <div class="mb-4">
      <div class="card shadow-sm" :class="{ 'opacity-50': loading.settings }">
        <div class="card-body position-relative py-3 d-flex flex-column">
          <div v-if="loading.settings" class="section-loader"></div>
          <h5 class="mb-1">Settings (Stock)</h5>
          <p class="text-secondary mb-2">
            Defina os níveis mínimos de stock a considerar para OCP, Farmas e
            Engel.
          </p>
          <div class="form-row mb-3 flex-grow-1">
            <div class="form-group col-md-4 mb-1">
              <label class="col-form-label">OCP Stock</label>
              <input
                type="number"
                v-model.number="settings.ocp_stock"
                class="form-control form-control-sm"
                :disabled="loading.settings"
              />
            </div>
            <div class="form-group col-md-4 mb-1">
              <label class="col-form-label">Farmas Stock</label>
              <input
                type="number"
                v-model.number="settings.farmas_stock"
                class="form-control form-control-sm"
                :disabled="loading.settings"
              />
            </div>
            <div class="form-group col-md-4 mb-1">
              <label class="col-form-label">Engel Stock</label>
              <input
                type="number"
                v-model.number="settings.engel_stock"
                class="form-control form-control-sm"
                :disabled="loading.settings"
              />
            </div>
          </div>
          <button
            class="btn btn-primary btn-sm mt-auto"
            @click="updateSettings"
            :disabled="loading.settings"
          >
            <span
              v-if="loading.settings"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Guardar
          </button>
        </div>
      </div>

      <!-- Info da fórmula -->
      <div class="alert alert-info mt-3 small">
        <strong>Fórmula do preço:</strong>
        <pre class="mb-2" style="white-space: pre-wrap">
            new_price = ((prod.p_custo + <u>margin</u>) / (1 - <u>stabilizer</u> / 100) 
           + <u>ship_interval.shipping</u> + <u>shipping_constant</u>)
           * ((1 + <u>tax_iva</u> / 100) / (1 - <u>payment</u> / 100))
        </pre>
        <ul class="mb-0">
          <li><code>prod.p_custo</code>: preço de custo do produto (base)</li>
          <li>
            <code>margin</code>: margem absoluta do intervalo (campo
            <em>Margin</em>)
          </li>
          <li>
            <code>stabilizer</code>: percentagem para estabilizar o preço (campo
            <em>Stabilizer</em>)
          </li>
          <li>
            <code>ship_interval.shipping</code>: custo de envio definido pelo
            intervalo de shipping
          </li>
          <li>
            <code>shipping_constant</code>: custo fixo adicional por intervalo
            (campo <em>Ship.Const.</em>)
          </li>
          <li><code>tax_iva</code>: IVA em %</li>
          <li>
            <code>payment</code>: % de custo de pagamento (campo
            <em>Payment</em>)
          </li>
        </ul>
      </div>
    </div>

    <!-- Accordion Sections -->
    <div class="accordion">
      <!-- Price Intervals -->
      <div class="accordion-item">
        <div class="accordion-header" @click="toggleSection('intervals')">
          <h5 class="mb-0">Price Intervals</h5>
          <i
            class="fas"
            :class="expanded.intervals ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.intervals" class="accordion-body">
            <!-- Column headers -->
            <div class="d-flex mb-2 font-weight-bold align-items-center">
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Start
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                End
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Margin
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Stabilizer
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Ship.Const.
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Payment
              </div>
              <div class="btn btn-sm btn-outline-secondary mr-1 disabled">
                ✓
              </div>
              <div class="btn btn-sm btn-outline-secondary disabled">✕</div>
            </div>

            <!-- Rows -->
            <div
              v-for="interval in intervals"
              :key="interval.id"
              class="d-flex mb-1 align-items-center"
            >
              <input
                type="number"
                v-model.number="interval.price_ini"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Start"
              />
              <input
                type="number"
                v-model.number="interval.price_fin"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="End"
              />
              <input
                type="number"
                v-model.number="interval.margin"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Margin"
              />
              <input
                type="number"
                v-model.number="interval.stabilizer"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Stabilizer"
              />
              <input
                type="number"
                v-model.number="interval.shipping_constant"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Ship.Const."
              />
              <input
                type="number"
                v-model.number="interval.payment"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Payment"
              />
              <button
                class="btn btn-sm btn-success mr-1"
                @click="updateInterval(interval)"
                :disabled="loading.intervals"
              >
                ✓
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="deleteInterval(interval.id)"
                :disabled="loading.intervals"
              >
                ✕
              </button>
            </div>

            <!-- New row -->
            <div class="d-flex">
              <input
                type="number"
                v-model.number="newInterval.price_ini"
                class="form-control form-control-sm mr-1"
                placeholder="Start"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.price_fin"
                class="form-control form-control-sm mr-1"
                placeholder="End"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.margin"
                class="form-control form-control-sm mr-1"
                placeholder="Margin"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.stabilizer"
                class="form-control form-control-sm mr-1"
                placeholder="Stabilizer"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.shipping_constant"
                class="form-control form-control-sm mr-1"
                placeholder="Ship.Const."
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.payment"
                class="form-control form-control-sm mr-1"
                placeholder="Payment"
                :disabled="loading.intervals"
              />
              <button
                class="btn btn-primary btn-sm"
                @click="addInterval"
                :disabled="loading.intervals || !intervalFilled(newInterval)"
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Shipping -->
      <div class="accordion-item">
        <div class="accordion-header" @click="toggleSection('shipping')">
          <h5 class="mb-0">Shipping</h5>
          <i
            class="fas"
            :class="expanded.shipping ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.shipping" class="accordion-body">
            <p class="text-secondary mb-2">
              Defina regras de envio com limites mínimos e máximos e o custo
              associado.
            </p>

            <!-- Headers -->
            <div class="d-flex mb-2 font-weight-bold align-items-center">
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Min
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Max
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Shipping
              </div>
              <div class="btn btn-sm btn-outline-secondary mr-1 disabled">
                ✓
              </div>
              <div class="btn btn-sm btn-outline-secondary disabled">✕</div>
            </div>

            <!-- Rows -->
            <div
              v-for="ship in shippings"
              :key="ship.id"
              class="d-flex mb-1 align-items-center"
            >
              <input
                type="number"
                v-model.number="ship.min"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Min"
              />
              <input
                type="number"
                v-model.number="ship.max"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Max"
              />
              <input
                type="number"
                v-model.number="ship.shipping"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Shipping"
              />
              <button
                class="btn btn-sm btn-success mr-1"
                @click="updateShipping(ship)"
                :disabled="loading.shipping"
              >
                ✓
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="deleteShipping(ship.id)"
                :disabled="loading.shipping"
              >
                ✕
              </button>
            </div>

            <!-- New row -->
            <div class="d-flex">
              <input
                type="number"
                v-model.number="newShipping.min"
                class="form-control form-control-sm mr-1"
                placeholder="Min"
                :disabled="loading.shipping"
              />
              <input
                type="number"
                v-model.number="newShipping.max"
                class="form-control form-control-sm mr-1"
                placeholder="Max"
                :disabled="loading.shipping"
              />
              <input
                type="number"
                v-model.number="newShipping.shipping"
                class="form-control form-control-sm mr-1"
                placeholder="Shipping"
                :disabled="loading.shipping"
              />
              <button
                class="btn btn-primary btn-sm"
                @click="addShipping"
                :disabled="loading.shipping || !shippingFilled(newShipping)"
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ShopifySettingsPage',
  data() {
    return {
      // O /eu primeiro; a Shopify fica no fim enquanto a loja estiver a vender
      sites: ['idivia_eu', 'idivia.eu'],
      siteLabels: {
        idivia_eu: 'WooCommerce (eu)',
        'idivia.eu': 'Shopify (idivia.eu)',
      },
      site: 'idivia_eu',

      // settings (stock)
      settings: { ocp_stock: 0, farmas_stock: 0, engel_stock: 0 },

      // price intervals
      intervals: [],
      newInterval: {
        price_ini: null,
        price_fin: null,
        margin: null,
        stabilizer: 1,
        shipping_constant: 0,
        payment: 0,
      },

      // shipping
      shippings: [],
      newShipping: { min: null, max: null, shipping: null },

      // loaders
      loading: {
        global: false,
        sites: false,
        settings: false,
        intervals: false,
        shipping: false,
      },

      expanded: { intervals: false, shipping: false },
    };
  },
  methods: {
    // Validadores (aceitam 0 como válido)
    isNum(v) {
      return v === 0 || (typeof v === 'number' && Number.isFinite(v));
    },
    intervalFilled(i) {
      return (
        this.isNum(i.price_ini) &&
        this.isNum(i.price_fin) &&
        this.isNum(i.margin) &&
        this.isNum(i.stabilizer) &&
        this.isNum(i.shipping_constant) &&
        this.isNum(i.payment)
      );
    },
    shippingFilled(s) {
      return this.isNum(s.min) && this.isNum(s.max) && this.isNum(s.shipping);
    },

    // SweetAlert helpers
    async confirmAdd(msg) {
      const r = await this.$swal.fire({
        title: 'Adicionar?',
        text: msg,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return r.isConfirmed;
    },
    async confirmUpdate(msg) {
      const r = await this.$swal.fire({
        title: 'Atualizar?',
        text: msg,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return r.isConfirmed;
    },
    async confirmDelete(msg) {
      const r = await this.$swal.fire({
        title: 'Apagar?',
        text: msg,
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return r.isConfirmed;
    },

    toggleSection(k) {
      this.expanded[k] = !this.expanded[k];
    },

    onSiteChange() {
      this.$router.replace({ params: { site: this.site } }).catch(() => {});
      this.loadAll();
    },

    async loadAll() {
      this.loading.global = true;
      await Promise.all([
        this.loadSection('settings', this.fetchSettings),
        this.loadSection('intervals', this.fetchIntervals),
        this.loadSection('shipping', this.fetchShippings),
      ]);
      this.loading.global = false;
    },

    async loadSection(k, fn) {
      this.loading[k] = true;
      try {
        await fn();
      } finally {
        this.loading[k] = false;
      }
    },

    // ---------- SETTINGS (STOCK) ----------
    async fetchSettings() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}shopify/settings/stock/${this.site}`
        );
        const row = data?.data?.settings?.[0] || {
          ocp_stock: 0,
          farmas_stock: 0,
          engel_stock: 0,
        };
        this.settings = row;
      } catch (e) {
        console.error(e);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar definições',
        });
      }
    },
    async updateSettings() {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar as definições de stock?'
      );
      if (!ok) return;
      this.loading.settings = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}shopify/settings/stock/${this.site}`,
          { settings: this.settings }
        );
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Settings saved',
          timer: 2000,
          position: 'top-end',
        });
      } catch (e) {
        console.error(e);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar definições',
        });
      } finally {
        this.loading.settings = false;
      }
    },

    // ---------- PRICE INTERVALS ----------
    async fetchIntervals() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}shopify/settings/price/${this.site}`
        );
        this.intervals = data?.data?.priceInterval || [];
      } catch (e) {
        console.error(e);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar intervalos',
        });
      }
    },
    async addInterval() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este intervalo?'
      );
      if (!ok) return;
      if (!this.intervalFilled(this.newInterval)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Preencha todos os campos (0 é válido).',
        });
        return;
      }
      this.loading.intervals = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}shopify/settings/price/${this.site}`,
          { interval: this.newInterval }
        );
        // limpar inputs depois de adicionar
        this.newInterval = {
          price_ini: null,
          price_fin: null,
          margin: null,
          stabilizer: 1,
          shipping_constant: 0,
          payment: 0,
        };
        await this.fetchIntervals();
      } catch (e) {
        console.error(e);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao adicionar intervalo',
        });
      } finally {
        this.loading.intervals = false;
      }
    },
    async updateInterval(interval) {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar este intervalo?'
      );
      if (!ok) return;
      this.loading.intervals = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}shopify/settings/price/${this.site}`,
          { interval }
        );
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Interval updated',
          timer: 2000,
          position: 'top-end',
        });
      } catch (e) {
        console.error(e);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar intervalo',
        });
      } finally {
        this.loading.intervals = false;
      }
    },
    async deleteInterval(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este intervalo?'
      );
      if (!ok) return;
      this.loading.intervals = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}shopify/settings/price/${this.site}`,
          { data: { id } } // controller lê id do body
        );
        await this.fetchIntervals();
      } catch (e) {
        console.error(e);
        this.$swal.fire({ icon: 'error', title: 'Erro ao eliminar intervalo' });
      } finally {
        this.loading.intervals = false;
      }
    },

    // ---------- SHIPPING ----------
    async fetchShippings() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}shopify/settings/shipping/${this.site}`
        );
        this.shippings = data?.data?.shipping || [];
      } catch (e) {
        console.error(e);
        this.$swal.fire({ icon: 'error', title: 'Erro ao carregar envios' });
      }
    },
    async addShipping() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este envio?'
      );
      if (!ok) return;
      if (!this.shippingFilled(this.newShipping)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Preencha Min, Max e Shipping (0 é válido).',
        });
        return;
      }
      this.loading.shipping = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}shopify/settings/shipping/${this.site}`,
          { shipping: this.newShipping }
        );
        // limpar inputs depois de adicionar
        this.newShipping = { min: null, max: null, shipping: null };
        await this.fetchShippings();
      } catch (e) {
        console.error(e);
        this.$swal.fire({ icon: 'error', title: 'Erro ao adicionar envio' });
      } finally {
        this.loading.shipping = false;
      }
    },
    async updateShipping(ship) {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar este envio?'
      );
      if (!ok) return;
      this.loading.shipping = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}shopify/settings/shipping/${this.site}`,
          { shipping: ship } // ship precisa conter 'id'
        );
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Shipping updated',
          timer: 2000,
          position: 'top-end',
        });
      } catch (e) {
        console.error(e);
        this.$swal.fire({ icon: 'error', title: 'Erro ao atualizar envio' });
      } finally {
        this.loading.shipping = false;
      }
    },
    async deleteShipping(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este envio?'
      );
      if (!ok) return;
      this.loading.shipping = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}shopify/settings/shipping/${this.site}`,
          { data: { id } }
        );
        await this.fetchShippings();
      } catch (e) {
        console.error(e);
        this.$swal.fire({ icon: 'error', title: 'Erro ao eliminar envio' });
      } finally {
        this.loading.shipping = false;
      }
    },
  },
  created() {
    this.loadAll();
  },
};
</script>

<style scoped>
.feed-config-page {
  font-family: 'Poppins', sans-serif;
  position: relative;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}
.section-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 5;
}
.accordion-item {
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}
.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f7f7f7;
  cursor: pointer;
}
.accordion-body {
  padding: 10px;
  background: white;
}
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
}
.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0;
}
.card-body.py-3 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
.alert pre {
  background: #f7fbff;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
}
</style>
